/**
 * Admin → Earn program → Volume rewards: campaigns (list and editor with a live cost check), rewards
 * waiting for a check, recent rewards with reversal, and clients blocked from volume rewards. Also the
 * per-client panel shown in Admin → Users. See VOLUME_REWARDS_PLAN.md.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  campaignTemplate,
  classCosts,
  costCheck,
  fmtUsd,
  stepReward,
  stepRewardShort,
  validateCampaign,
  volumeShort,
  VR_CADENCE_LABEL,
  VR_CADENCES,
  VR_CAMPAIGN_STATUSES,
  VR_CLASS_LABEL,
  VR_CLASSES,
  VR_KIND_LABEL,
  VR_KINDS,
  todayIn,
  type VrAdminCampaign,
  type VrAdminOverview,
  type VrAdminReward,
  type VrCadence,
  type VrCampaignInput,
  type VrKind,
  type VrReward,
  type VrRewardStatus,
  type VrStep,
  type VrUserStatus,
  type VrActiveBonus,
} from '@platform/shared';
import { DateField } from '../components/DateRange';
import { ClientCell, clientLabel } from '../components/ClientCell';
import { cx, Modal, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';

const input = 'h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted disabled:opacity-60';
const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const primary = 'h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';
const th = 'px-3 py-2 text-left text-[11px] font-medium text-muted whitespace-nowrap';
const td = 'px-3 py-2 whitespace-nowrap';
const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

function useGet<T>(path: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    if (!path) return;
    try {
      setData(await api.get<T>(path));
      setError(null);
    } catch (e) {
      setError(err(e));
    }
  }, [path]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, error, reload };
}

function Section({ title, sub, children, aside }: { title: string; sub?: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-line bg-panel p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="m-0 text-sm font-semibold">{title}</h2>
          {sub && <p className="m-0 mt-0.5 text-xs text-muted">{sub}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Kpi({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl border border-line bg-panel p-3.5">
      <span className="text-[11px] text-muted">{label}</span>
      <span className="num text-xl font-semibold">{value}</span>
      {sub && <span className="text-[11px] text-muted">{sub}</span>}
    </div>
  );
}

const STATUS_TONE: Record<string, string> = {
  live: 'bg-up-bg text-up',
  draft: 'bg-track text-muted',
  paused: 'bg-warn-bg text-warn',
  ended: 'bg-track text-muted',
  ready: 'bg-accent-bg text-accent-fg',
  held: 'bg-warn-bg text-warn',
  active: 'bg-up-bg text-up',
  finished: 'bg-track text-fg',
  expired: 'bg-track text-muted',
  rejected: 'bg-down-bg text-down',
  reversed: 'bg-down-bg text-down',
};
const Tag = ({ s, children }: { s: string; children?: ReactNode }) => <span className={cx('rounded px-1.5 py-0.5 text-[11px] font-semibold capitalize', STATUS_TONE[s])}>{children ?? s}</span>;

const REASON_LABEL: Record<string, string> = { over_threshold: 'Over the check threshold', referral_check: 'Flagged by the referral check' };

/** What a campaign's period is, in a few words. */
function runsText(c: VrCampaignInput): string {
  switch (c.cadence) {
    case 'welcome':
      return `First ${c.rules.welcomeDays} days after the first deposit`;
    case 'daily':
      return 'Every day, 00:00 UTC';
    case 'weekly':
      return 'Every week, Monday 00:00 UTC';
    case 'monthly':
      return 'Every month, the 1st 00:00 UTC';
    case 'event':
      return `${day(c.startsAt)} to ${c.endsAt ? day(c.endsAt) : '…'}`;
  }
}

const day = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
/** "27 Oct, 14:20", in the admin's time. */
const dayTime = (iso: string | null) => (iso ? `${day(iso)}, ${new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : '—');

const budgetPeriod = (c: VrCadence) => (c === 'daily' ? 'a day' : c === 'weekly' ? 'a week' : c === 'event' ? 'in total' : 'a month');

// ---------- dates in the editor (local time, stored as ISO) ----------

const pad = (n: number) => String(n).padStart(2, '0');
function toLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const fromLocal = (v: string) => (v ? new Date(v).toISOString() : '');

// ---------- campaign editor ----------

function NumField({ label, value, onChange, disabled, suffix, step = 'any', placeholder, className }: { label: string; value: number | null; onChange: (v: number | null) => void; disabled?: boolean; suffix?: string; step?: string; placeholder?: string; className?: string }) {
  return (
    <label className={cx('flex min-w-0 flex-col gap-1 text-[11px] text-muted', className)}>
      {label}
      <span className="relative">
        <input
          type="number"
          step={step}
          disabled={disabled}
          placeholder={placeholder}
          value={value === null || !Number.isFinite(value) ? '' : value}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className={cx(input, 'num', suffix && 'pr-9')}
        />
        {suffix && <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs text-muted">{suffix}</span>}
      </span>
    </label>
  );
}

function CostPanel({ form, data }: { form: VrCampaignInput; data: VrAdminOverview }) {
  const r = costCheck(form.steps, form.rules.weights, classCosts(data.instruments, form.rules.symbols));
  const tone = (x: number) => (x > 100 ? 'bg-down-bg text-down' : x > 50 ? 'bg-warn-bg text-warn' : 'bg-up-bg text-up');
  const pct = (x: number) => (Number.isFinite(x) ? `${Math.round(x)}%` : '∞');
  return (
    <div className="flex flex-col gap-2 rounded-lg bg-track p-3 text-xs">
      <div>
        <b>Cost check.</b> Cash and rebates pay at most <b className="num">{r.cashBps.toFixed(2)} bps</b> of counted volume
        {r.allBps > r.cashBps && (
          <>
            , <b className="num">{r.allBps.toFixed(2)} bps</b> with trading bonuses
          </>
        )}
        , at the richest step.
      </div>
      <div className="flex flex-wrap gap-1.5">
        {r.classes.map((c) => (
          <span key={c.assetClass} className={cx('num rounded px-1.5 py-0.5 font-semibold', tone(c.cashShare))} title={`Cheapest market costs ${c.costBps.toFixed(2)} bps per fill; counts at ${c.weight}%`}>
            {VR_CLASS_LABEL[c.assetClass]} {pct(c.cashShare)}
            {c.allShare > c.cashShare && <span className="font-normal opacity-80"> · {pct(c.allShare)} with bonus</span>}
          </span>
        ))}
        {!r.classes.length && <span className="text-muted">No counted class has a market to price.</span>}
      </div>
      <p className="m-0 text-muted">Share of the client's minimum trading cost (fee, half the minimum spread and markup, on the cheapest market in each class). Over 100% for cash or rebates can't be saved: trading only for rewards would make money. Bonuses may go over, since they can't be withdrawn.</p>
      {r.refused && <p className="m-0 font-semibold text-down">{r.refused}</p>}
    </div>
  );
}

function CampaignEditor({ campaign, cadence, data, onClose, onSaved }: { campaign: VrAdminCampaign | null; cadence: VrCadence; data: VrAdminOverview; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<VrCampaignInput>(() => (campaign ? structuredClone(campaign) : campaignTemplate(cadence)));
  const [symbolsRaw, setSymbolsRaw] = useState((campaign?.rules.symbols ?? []).join(', '));
  const [countriesRaw, setCountriesRaw] = useState((campaign?.eligibility.excludedCountries ?? []).join(', '));
  const [busy, setBusy] = useState(false);
  const [e, setE] = useState<string | null>(null);
  const locked = !!campaign?.started;
  const up = (patch: Partial<VrCampaignInput>) => setF((cur) => ({ ...cur, ...patch }));
  const upRules = (patch: Partial<VrCampaignInput['rules']>) => setF((cur) => ({ ...cur, rules: { ...cur.rules, ...patch } }));
  const upStep = (i: number, patch: Partial<VrStep>) =>
    setF((cur) => {
      const steps = cur.steps.map((s, j) => (j === i ? { ...s, ...patch } : s));
      const s = steps[i]!;
      // Keep only what the kind uses.
      steps[i] = { volume: s.volume, kind: s.kind, usd: s.usd, ...(s.kind === 'rebate' ? { pct: s.pct ?? 20 } : {}), ...(s.kind !== 'cash' ? { days: s.days ?? 7 } : {}) };
      return { ...cur, steps };
    });
  const tierIds = data.tiers.map((t) => t.id);
  const cost = costCheck(f.steps, f.rules.weights, classCosts(data.instruments, f.rules.symbols));
  const problem = validateCampaign(f, tierIds) ?? cost.refused;
  const statuses = VR_CAMPAIGN_STATUSES.filter((s) => !(locked && s === 'draft') && !(campaign?.status === 'ended' && s !== 'ended') && !(!campaign && s === 'ended'));

  const save = async () => {
    setBusy(true);
    setE(null);
    try {
      if (campaign) await api.put(`/api/admin/earn/volume/campaigns/${campaign.id}`, f);
      else await api.post('/api/admin/earn/volume/campaigns', f);
      onSaved();
      onClose();
    } catch (ex) {
      setE(err(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={campaign ? `Edit ${campaign.name}` : `New ${VR_CADENCE_LABEL[cadence].toLowerCase()} campaign`} width={760}>
      <div className="flex max-h-[75vh] flex-col gap-4 overflow-y-auto pr-1 text-xs">
        {locked && (
          <p className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-warn">
            This campaign has started, so what clients are working towards is locked: steps, how volume counts, who can join and the start. You can rename it, pause or end it, move its end date, or change the budget, claim days and check threshold.
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            Name
            <input className={input} value={f.name} maxLength={60} onChange={(ev) => up({ name: ev.target.value })} placeholder="Weekly volume" />
          </label>
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            Status
            <select className={input} value={f.status} onChange={(ev) => up({ status: ev.target.value as VrCampaignInput['status'] })}>
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {s === 'draft' ? 'Draft (hidden)' : s === 'live' ? 'Live' : s === 'paused' ? 'Paused (hidden, nothing accrues)' : 'Ended'}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[11px] text-muted sm:col-span-2">
            Description clients see
            <input className={input} value={f.description} maxLength={240} onChange={(ev) => up({ description: ev.target.value })} placeholder="Monday to Monday, 00:00 UTC. Every step pays USDT cash." />
          </label>
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            Runs
            <select className={input} disabled={locked} value={f.cadence} onChange={(ev) => {
              const c = ev.target.value as VrCadence;
              up({ cadence: c, endsAt: c === 'event' ? (f.endsAt ?? new Date(Date.now() + 14 * 86_400_000).toISOString()) : f.endsAt });
              upRules({ welcomeDays: c === 'welcome' ? (f.rules.welcomeDays ?? 30) : null });
            }}>
              {VR_CADENCES.map((c) => (
                <option key={c} value={c}>
                  {c === 'welcome' ? 'Welcome: days after the first deposit' : c === 'event' ? 'Event: set dates' : `${VR_CADENCE_LABEL[c]} (UTC)`}
                </option>
              ))}
            </select>
          </label>
          {f.cadence === 'welcome' ? (
            <NumField label="Days after the first deposit" step="1" disabled={locked} value={f.rules.welcomeDays} onChange={(v) => upRules({ welcomeDays: v })} />
          ) : (
            <div />
          )}
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            Starts (your time)
            <DateField withTime aria-label="Starts" className={input} disabled={locked} min={locked ? undefined : todayIn()} value={toLocal(f.startsAt)} onChange={(v) => up({ startsAt: fromLocal(v) })} />
          </label>
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            Ends (your time){f.cadence !== 'event' && ' · empty to repeat until ended'}
            <DateField withTime aria-label="Ends" className={input} value={toLocal(f.endsAt)} onChange={(v) => up({ endsAt: v ? fromLocal(v) : null })} />
          </label>
        </div>

        <fieldset className="m-0 flex flex-col gap-2 rounded-lg border border-line p-3" disabled={locked}>
          <legend className="px-1 text-[11px] font-semibold text-fg">How volume counts</legend>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            {VR_CLASSES.map((k) => (
              <NumField key={k} label={`${VR_CLASS_LABEL[k]} %`} step="1" disabled={locked} value={f.rules.weights[k]} onChange={(v) => upRules({ weights: { ...f.rules.weights, [k]: v ?? 0 } })} />
            ))}
          </div>
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_160px]">
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              Only these markets (empty = all)
              <input
                className={cx(input, 'num uppercase placeholder:normal-case')}
                disabled={locked}
                placeholder="e.g. GOLD, SILVER"
                value={symbolsRaw}
                onChange={(ev) => setSymbolsRaw(ev.target.value)}
                onBlur={() => upRules({ symbols: [...new Set(symbolsRaw.split(/[\s,]+/).map((x) => x.trim().toUpperCase()).filter(Boolean))] })}
              />
            </label>
            <NumField label="Minimum hold" suffix="sec" step="1" disabled={locked} value={f.rules.minHoldSeconds} onChange={(v) => upRules({ minHoldSeconds: v ?? 0 })} />
          </div>
          <p className="m-0 text-muted">Open and close fills count at notional. Positions closed sooner than the minimum hold, and positions that were part of a hedge, don't count.</p>
        </fieldset>

        <fieldset className="m-0 flex flex-col gap-2 rounded-lg border border-line p-3" disabled={locked}>
          <legend className="px-1 text-[11px] font-semibold text-fg">Steps · counted volume → reward (they add up)</legend>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] border-collapse">
              <thead>
                <tr>
                  {['Counted volume', 'Reward', 'Amount / limit', 'Rebate %', 'Lasts', ''].map((h) => (
                    <th key={h} className={cx(th, 'px-1')}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {f.steps.map((s, i) => (
                  <tr key={i}>
                    <td className="p-1">
                      <NumField label="" step="1000" disabled={locked} value={s.volume} onChange={(v) => upStep(i, { volume: v ?? NaN })} suffix="$" />
                    </td>
                    <td className="p-1">
                      <select className={cx(input, 'min-w-[150px]')} disabled={locked} value={s.kind} onChange={(ev) => upStep(i, { kind: ev.target.value as VrKind })} aria-label={`Step ${i + 1} reward`}>
                        {VR_KINDS.map((k) => (
                          <option key={k} value={k}>
                            {VR_KIND_LABEL[k]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-1">
                      <NumField label="" step="0.01" disabled={locked} value={s.usd} onChange={(v) => upStep(i, { usd: v ?? NaN })} suffix="$" />
                    </td>
                    <td className="p-1">{s.kind === 'rebate' ? <NumField label="" step="1" disabled={locked} value={s.pct ?? null} onChange={(v) => upStep(i, { pct: v ?? NaN })} suffix="%" /> : <span className="text-muted">—</span>}</td>
                    <td className="p-1">{s.kind !== 'cash' ? <NumField label="" step="1" disabled={locked} value={s.days ?? null} onChange={(v) => upStep(i, { days: v ?? NaN })} suffix="days" /> : <span className="text-muted">—</span>}</td>
                    <td className="p-1">
                      {!locked && f.steps.length > 1 && (
                        <button type="button" className={cx(btn, 'text-down')} onClick={() => setF((cur) => ({ ...cur, steps: cur.steps.filter((_, j) => j !== i) }))}>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!locked && f.steps.length < 8 && (
            <button
              type="button"
              className={cx(btn, 'self-start')}
              onClick={() => setF((cur) => ({ ...cur, steps: [...cur.steps, { volume: (cur.steps[cur.steps.length - 1]?.volume ?? 0) * 5 || 10_000, kind: 'cash', usd: 10 }] }))}
            >
              Add step
            </button>
          )}
        </fieldset>

        <CostPanel form={f} data={data} />

        <div className="grid gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            Lowest tier that joins
            <select className={input} disabled={locked} value={f.eligibility.minTier ?? ''} onChange={(ev) => up({ eligibility: { ...f.eligibility, minTier: ev.target.value || null } })}>
              <option value="">Everyone</option>
              {data.tiers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} and above
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[11px] text-muted sm:col-span-2">
            Not for countries
            <input
              className={cx(input, 'num uppercase placeholder:normal-case')}
              disabled={locked}
              placeholder="None, or e.g. GB, BE"
              value={countriesRaw}
              onChange={(ev) => setCountriesRaw(ev.target.value)}
              onBlur={() => up({ eligibility: { ...f.eligibility, excludedCountries: [...new Set(countriesRaw.split(/[\s,]+/).map((x) => x.trim().toUpperCase()).filter(Boolean))] } })}
            />
          </label>
          <NumField label="Days to claim" step="1" value={f.claimDays} onChange={(v) => up({ claimDays: v ?? NaN })} />
          <NumField label="Check rewards from" suffix="$" placeholder="Never" value={f.holdAbove} onChange={(v) => up({ holdAbove: v })} />
          <NumField label={`Budget, ${budgetPeriod(f.cadence)}`} suffix="$" placeholder="No limit" value={f.budgetUsd} onChange={(v) => up({ budgetUsd: v })} />
        </div>
        <p className="m-0 text-muted">
          Clients claim each reached step within the claim days. Rewards at or above the check amount, and rewards for clients the referral check flagged, wait in Rewards to check. When the budget is used up, steps not yet reached stop paying until the next period.
        </p>
        {(e || problem) && <p className="m-0 text-down">{e ?? problem}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primary} disabled={busy || !!problem} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------- reason prompt ----------

function ReasonModal({ title, text, action, danger, required = true, onClose, onSubmit }: { title: string; text: string; action: string; danger?: boolean; required?: boolean; onClose: () => void; onSubmit: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [e, setE] = useState<string | null>(null);
  return (
    <Modal open onClose={onClose} title={title} width={440}>
      <form
        className="flex flex-col gap-3 text-xs"
        onSubmit={async (ev) => {
          ev.preventDefault();
          setBusy(true);
          setE(null);
          try {
            await onSubmit(reason.trim());
            onClose();
          } catch (ex) {
            setE(err(ex));
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="m-0 text-muted">{text}</p>
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          Reason{required ? '' : ' (optional)'}
          <textarea className={cx(input, 'h-20 py-2')} maxLength={500} value={reason} onChange={(ev) => setReason(ev.target.value)} />
        </label>
        {e && <p className="m-0 text-down">{e}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className={cx(primary, danger && '!bg-down')} disabled={busy || (required && !reason.trim())}>
            {busy ? 'Working…' : action}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- the section ----------

const rewardText = (r: VrReward) => stepReward({ kind: r.kind, usd: Number(r.amount), pct: r.pct ?? undefined });
const reversible = (r: VrReward) => r.status === 'ready' || r.status === 'held' || r.status === 'active' || r.status === 'finished';

function RewardsTable({ rows, onReverse, onDecide }: { rows: VrAdminReward[]; onReverse?: (r: VrAdminReward) => void; onDecide?: (r: VrAdminReward, d: 'approve' | 'reject') => void }) {
  if (!rows.length) return <p className="m-0 text-xs text-muted">Nothing here.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[820px] border-collapse text-xs">
        <thead>
          <tr>
            {['Reached', 'Client', 'Campaign', 'Step', 'Reward', 'Status', onDecide ? 'Why held' : 'Detail', ''].map((h) => (
              <th key={h} className={th}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-line">
              <td className={cx(td, 'num text-muted')}>{dayTime(r.reachedAt)}</td>
              <td className={td}>
                <ClientCell name={r.name} email={r.email} crmId={r.externalClientId} />
              </td>
              <td className={td} title={`Period from ${r.period}`}>
                {r.campaign}
              </td>
              <td className={cx(td, 'num')}>{volumeShort(r.stepVolume)}</td>
              <td className={td}>{rewardText(r)}</td>
              <td className={td}>
                <Tag s={r.status} />
              </td>
              <td className={cx(td, 'max-w-[240px] truncate text-muted')}>
                {onDecide
                  ? r.reasons.map((x) => REASON_LABEL[x] ?? x).join(' · ')
                  : r.status === 'active' && r.kind === 'rebate'
                    ? `${fmtUsd(r.paid)} paid of ${fmtUsd(r.amount)}`
                    : r.status === 'active' && r.kind === 'bonus'
                      ? `${fmtUsd(r.remaining)} on the account until ${dayTime(r.activeUntil)}`
                      : r.status === 'ready'
                        ? `Claim by ${dayTime(r.claimBy)}`
                        : (r.note ?? (r.claimedAt ? `Claimed ${dayTime(r.claimedAt)}` : ''))}
              </td>
              <td className={cx(td, 'flex gap-1.5')}>
                {onDecide && (
                  <>
                    <button type="button" className={cx(btn, 'text-up')} onClick={() => onDecide(r, 'approve')}>
                      Approve
                    </button>
                    <button type="button" className={cx(btn, 'text-down')} onClick={() => onDecide(r, 'reject')}>
                      Reject
                    </button>
                  </>
                )}
                {onReverse && reversible(r) && (
                  <button type="button" className={cx(btn, 'text-down')} onClick={() => onReverse(r)}>
                    {r.status === 'ready' || r.status === 'held' ? 'Cancel' : 'Reverse'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const FILTERS: { id: '' | VrRewardStatus; label: string }[] = [
  { id: '', label: 'All' },
  { id: 'ready', label: 'To claim' },
  { id: 'active', label: 'In use' },
  { id: 'finished', label: 'Finished' },
  { id: 'expired', label: 'Expired' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'reversed', label: 'Reversed' },
];

export function VolumeRewardsAdmin() {
  const { data, error, reload } = useGet<VrAdminOverview>('/api/admin/earn/volume');
  const held = useGet<VrAdminReward[]>('/api/admin/earn/volume/rewards?status=held');
  const [filter, setFilter] = useState<'' | VrRewardStatus>('');
  const recent = useGet<VrAdminReward[]>(`/api/admin/earn/volume/rewards${filter ? `?status=${filter}` : ''}`);
  const [editing, setEditing] = useState<{ campaign: VrAdminCampaign | null; cadence: VrCadence } | null>(null);
  const [newCadence, setNewCadence] = useState<VrCadence>('event');
  const [prompt, setPrompt] = useState<{ title: string; text: string; action: string; danger?: boolean; required?: boolean; run: (reason: string) => Promise<void> } | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  if (!data) return error ? <p className="text-sm text-down">{error}</p> : <Spinner />;
  const refresh = async (text: string) => {
    setMsg({ ok: true, text });
    await Promise.all([reload(), held.reload(), recent.reload()]);
  };
  const live = data.campaigns.filter((c) => c.status === 'live' && c.started);
  const scheduled = data.campaigns.filter((c) => c.status === 'live' && !c.started);
  const who = (c: { name: string | null; email: string; externalClientId: string | null }) => clientLabel({ name: c.name, email: c.email, crmId: c.externalClientId });
  const decide = (r: VrAdminReward, d: 'approve' | 'reject') =>
    setPrompt({
      title: d === 'approve' ? `Approve ${rewardText(r)}?` : `Reject ${rewardText(r)}?`,
      text: d === 'approve' ? `${who(r)} can claim it for the campaign's full claim period from now, and is told it's ready.` : `${who(r)} won't be able to claim it. Say why, for the record.`,
      action: d === 'approve' ? 'Approve' : 'Reject',
      danger: d === 'reject',
      required: d === 'reject',
      run: async (note) => {
        await api.post(`/api/admin/earn/volume/rewards/${r.id}/decide`, { decision: d, note });
        await refresh(d === 'approve' ? 'Approved.' : 'Rejected.');
      },
    });
  const reverse = (r: VrAdminReward) =>
    setPrompt({
      title: `${r.status === 'ready' || r.status === 'held' ? 'Cancel' : 'Reverse'} ${rewardText(r)} for ${who(r)}?`,
      text:
        r.status === 'ready' || r.status === 'held'
          ? "It hasn't been paid, so it's cancelled."
          : r.kind === 'cash'
            ? 'The cash is debited from what the client can withdraw now. If they no longer have it, nothing is taken.'
            : r.kind === 'bonus'
              ? 'The bonus credit comes off the account now; any part backing open positions comes off when they close.'
              : 'The rebate stops now. What it already paid stays.',
      action: r.status === 'ready' || r.status === 'held' ? 'Cancel reward' : 'Reverse',
      danger: true,
      run: async (reason) => {
        await api.post(`/api/admin/earn/volume/rewards/${r.id}/reverse`, { reason });
        await refresh('Done. The client has been told.');
      },
    });
  const unblock = (b: VrAdminOverview['blocked'][number]) =>
    setPrompt({
      title: `Unblock ${who(b)}?`,
      text: 'They can earn and claim volume rewards again from now.',
      action: 'Unblock',
      run: async (reason) => {
        await api.put(`/api/admin/earn/volume/users/${b.userId}/status`, { status: 'enabled', reason });
        await refresh('Unblocked.');
      },
    });

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-2.5 sm:grid-cols-4">
        <Kpi label="Live campaigns" value={live.length} sub={scheduled.length ? `${scheduled.length} scheduled` : undefined} />
        <Kpi label="Paid all time" value={fmtUsd(data.campaigns.reduce((t, c) => t + Number(c.stats.paid), 0).toFixed(2))} sub="Cash and bonuses claimed" />
        <Kpi label="To check" value={data.held} sub={data.held ? 'Below' : 'Nothing waiting'} />
        <Kpi label="Blocked clients" value={data.blocked.length} />
      </div>
      {msg && <p className={cx('m-0 text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</p>}

      <Section
        title="Campaigns"
        sub="Clients see live campaigns while Volume rewards is on in Admin → Features. Steps and rules lock once a campaign starts."
        aside={
          <div className="flex items-center gap-2">
            <select className={cx(input, '!h-8 !w-auto')} value={newCadence} onChange={(e) => setNewCadence(e.target.value as VrCadence)} aria-label="Kind of campaign">
              {VR_CADENCES.map((c) => (
                <option key={c} value={c}>
                  {VR_CADENCE_LABEL[c]}
                </option>
              ))}
            </select>
            <button type="button" className={primary} onClick={() => setEditing({ campaign: null, cadence: newCadence })}>
              New campaign
            </button>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[780px] border-collapse text-xs">
            <thead>
              <tr>
                {['Campaign', 'Runs', 'Steps', 'Status', 'Reached', 'Paid', 'Budget used', ''].map((h) => (
                  <th key={h} className={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.campaigns.map((c) => {
                const used = c.budgetUsd ? Math.min(100, (Number(c.stats.budgetUsed) / c.budgetUsd) * 100) : null;
                return (
                  <tr key={c.id} className={cx('border-t border-line', (c.status === 'ended' || c.status === 'draft') && 'opacity-70')}>
                    <td className={td}>
                      <b>{c.name}</b>
                      {c.eligibility.minTier && <span className="text-muted"> · {data.tiers.find((t) => t.id === c.eligibility.minTier)?.name ?? c.eligibility.minTier}+</span>}
                    </td>
                    <td className={cx(td, 'max-w-[170px] min-w-[120px] !whitespace-normal text-muted')}>{runsText(c)}</td>
                    <td className={cx(td, 'max-w-[190px] truncate')} title={c.steps.map((s) => `${volumeShort(s.volume)} → ${stepReward(s)}`).join(' · ')}>
                      {c.steps.map((s) => `${volumeShort(s.volume)} ${stepRewardShort(s)}`).join(' · ')}
                    </td>
                    <td className={td}>{c.status === 'live' && !c.started ? <Tag s="ready">Scheduled</Tag> : <Tag s={c.status} />}</td>
                    <td className={cx(td, 'num')}>
                      {c.stats.reached.toLocaleString('en-US')}
                      {c.stats.held > 0 && <span className="text-warn"> · {c.stats.held} to check</span>}
                    </td>
                    <td className={cx(td, 'num')}>{fmtUsd(c.stats.paid)}</td>
                    <td className={td}>
                      {used === null ? (
                        <span className="text-muted">No limit</span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <span className="h-1.5 w-10 overflow-hidden rounded-full bg-track">
                            <span className={cx('block h-full rounded-full', used >= 100 ? 'bg-down' : 'bg-accent')} style={{ width: `${used}%` }} />
                          </span>
                          <span className="num" title={`${fmtUsd(c.stats.budgetUsed)} of ${fmtUsd(String(c.budgetUsd))} ${budgetPeriod(c.cadence)}`}>
                            {fmtUsd(c.stats.budgetUsed, 0)} / {fmtUsd(String(c.budgetUsd), 0)}
                          </span>
                        </span>
                      )}
                    </td>
                    <td className={td}>
                      <button type="button" className={btn} onClick={() => setEditing({ campaign: c, cadence: c.cadence })}>
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Rewards to check" sub="Held because they're at or above the campaign's check amount, or the client was flagged by the referral check. Approving gives the client the full claim period from now.">
        <RewardsTable rows={held.data ?? []} onDecide={decide} />
      </Section>

      <Section
        title="Recent rewards"
        sub="Cancel a reward that hasn't been paid, or reverse one that has. The client is told."
        aside={
          <select className={cx(input, '!h-8 !w-auto')} value={filter} onChange={(e) => setFilter(e.target.value as '' | VrRewardStatus)} aria-label="Show rewards">
            {FILTERS.map((x) => (
              <option key={x.id} value={x.id}>
                {x.label}
              </option>
            ))}
          </select>
        }
      >
        <RewardsTable rows={recent.data ?? []} onReverse={reverse} />
      </Section>

      <Section title="Blocked clients" sub="A blocked client earns no new rewards and can't claim. Block from the client's page in Users, or from the CRM.">
        {data.blocked.length === 0 ? (
          <p className="m-0 text-xs text-muted">Nobody is blocked.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse text-xs">
              <thead>
                <tr>
                  {['Client', 'Reason', 'Since', ''].map((h) => (
                    <th key={h} className={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.blocked.map((b) => (
                  <tr key={b.userId} className="border-t border-line">
                    <td className={td}>
                      <ClientCell name={b.name} email={b.email} crmId={b.externalClientId} />
                    </td>
                    <td className={cx(td, 'max-w-[320px] truncate text-muted')}>{b.reason ?? '—'}</td>
                    <td className={cx(td, 'num text-muted')}>{b.at ? new Date(b.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                    <td className={td}>
                      <button type="button" className={btn} onClick={() => unblock(b)}>
                        Unblock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {editing && <CampaignEditor campaign={editing.campaign} cadence={editing.cadence} data={data} onClose={() => setEditing(null)} onSaved={() => void refresh('Saved.')} />}
      {prompt && <ReasonModal {...prompt} onClose={() => setPrompt(null)} onSubmit={prompt.run} />}
    </div>
  );
}

// ---------- Admin → Users ----------

interface ClientVolume {
  status: VrUserStatus;
  reason: string | null;
  changedAt: string | null;
  activeBonus: VrActiveBonus;
  rewards: VrReward[];
}

/** One client's volume rewards and the block switch, under their Rewards panel in Admin → Users. */
export function UserVolumePanel({ userId }: { userId: string }) {
  const { data, error, reload } = useGet<ClientVolume>(`/api/admin/earn/volume/users/${userId}`);
  const [asking, setAsking] = useState(false);
  if (!data) return error ? <p className="m-0 text-xs text-down">{error}</p> : <Spinner />;
  const blocked = data.status === 'blocked';
  const claimed = data.rewards.filter((r) => r.claimedAt && r.kind !== 'rebate' && r.status !== 'reversed').reduce((t, r) => t + Number(r.amount), 0);
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-sm">Volume rewards</b>
        {blocked ? <Tag s="rejected">Blocked</Tag> : <Tag s="live">Can earn</Tag>}
        {blocked && data.reason && <span className="text-muted">{data.reason}</span>}
        <span className="flex-1" />
        <button type="button" className={cx(btn, !blocked && 'text-down')} onClick={() => setAsking(true)}>
          {blocked ? 'Unblock' : 'Block'}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {(
          [
            ['To claim', String(data.rewards.filter((r) => r.status === 'ready').length)],
            ['To check', String(data.rewards.filter((r) => r.status === 'held').length)],
            ['Bonus on account', fmtUsd(data.activeBonus.amount)],
            ['Claimed (last 50)', fmtUsd(claimed.toFixed(2))],
          ] as const
        ).map(([l, v]) => (
          <div key={l} className="rounded-md bg-raised px-2.5 py-1.5">
            <div className="text-[10.5px] text-muted">{l}</div>
            <div className="num font-semibold">{v}</div>
          </div>
        ))}
      </div>
      {data.rewards.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {data.rewards.slice(0, 5).map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-2">
              <span className="num text-muted">{dayTime(r.reachedAt)}</span>
              <span>
                {r.campaign} · {volumeShort(r.stepVolume)} → {rewardText(r)}
              </span>
              <Tag s={r.status} />
            </li>
          ))}
        </ul>
      )}
      {asking && (
        <ReasonModal
          title={blocked ? 'Unblock volume rewards?' : 'Block volume rewards?'}
          text={blocked ? 'The client can earn and claim volume rewards again from now.' : "The client earns no new rewards and can't claim the ones waiting. The CRM is told."}
          action={blocked ? 'Unblock' : 'Block'}
          danger={!blocked}
          onClose={() => setAsking(false)}
          onSubmit={async (reason) => {
            await api.put(`/api/admin/earn/volume/users/${userId}/status`, { status: blocked ? 'enabled' : 'blocked', reason });
            await reload();
          }}
        />
      )}
    </div>
  );
}
